/**
 * Futures Data Formatting Utilities
 *
 * Formatting functions for futures market data including funding rates,
 * funding timers, and mark prices.
 */

/**
 * Format an operator-configured funding-rate ratio as a percentage without
 * rounding away the supported 0.001% input precision.
 *
 * Configured thresholds are control values, so their display must visibly
 * round-trip values such as 0.00005 (0.005%) instead of presenting 0.01%.
 */
export function formatConfiguredFundingRate(fundingRate: number): string {
  const percentage = fundingRate * 100;
  const normalizedPercentage = Object.is(percentage, -0) ? 0 : percentage;
  const formattedPercentage = normalizedPercentage
    .toFixed(6)
    .replace(/\.?0+$/, '');

  return `${formattedPercentage}%`;
}

/**
 * Format funding rate from decimal to percentage
 * @param fundingRate - Decimal funding rate (e.g., 0.0001)
 * @returns Formatted percentage string (e.g., "0.01%")
 *
 * @example
 * formatFundingRate(0.0001) // "0.01%"
 * formatFundingRate(0.000123) // "0.0123%"
 * formatFundingRate(-0.0005) // "-0.05%"
 */
export function formatFundingRate(fundingRate: number): string {
  // Convert decimal to percentage (multiply by 100)
  const percentage = fundingRate * 100;

  // Determine decimal places based on magnitude
  // - For zero, use 2 decimals
  // - For values >= 0.01%, use 2 decimals
  // - For values < 0.01%, use 4 decimals to show precision
  const decimals = percentage === 0 || Math.abs(percentage) >= 0.01 ? 2 : 4;

  return `${percentage.toFixed(decimals)}%`;
}

/**
 * Get color for funding rate display
 * @param fundingRate - Decimal funding rate
 * @returns CSS color string
 *
 * @example
 * getFundingRateColor(0.0001) // "#4caf50" (green - longs pay shorts)
 * getFundingRateColor(-0.0001) // "#ff9800" (orange - shorts pay longs)
 * getFundingRateColor(0) // "rgba(255, 255, 255, 0.87)" (white - neutral)
 */
export function getFundingRateColor(fundingRate: number): string {
  if (fundingRate > 0) {
    return '#4caf50'; // Green - longs pay shorts
  } else if (fundingRate < 0) {
    return '#ff9800'; // Orange - shorts pay longs
  } else {
    return 'rgba(255, 255, 255, 0.87)'; // White - neutral
  }
}

/**
 * Format countdown timer from timestamp
 * @param targetTimestamp - Unix timestamp in milliseconds
 * @returns Formatted countdown string (e.g., "2h 34m", "45m", "Next: 16:00 UTC")
 *
 * @example
 * formatFundingTimer(Date.now() + 9000000) // "2h 30m"
 * formatFundingTimer(Date.now() + 2700000) // "45m"
 * formatFundingTimer(Date.now() + 300000) // "5m"
 */
export function formatFundingTimer(targetTimestamp: number): string {
  const now = Date.now();
  const diff = targetTimestamp - now;

  // If time has passed or is very close (< 1 minute), show "Next: HH:MM UTC"
  if (diff < 60000) {
    const date = new Date(targetTimestamp);
    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    return `Next: ${hours}:${minutes} UTC`;
  }

  // Calculate hours and minutes remaining
  const totalMinutes = Math.floor(diff / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  // Format based on time remaining
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
}

/**
 * Get full precision funding rate for tooltip
 * @param fundingRate - Decimal funding rate
 * @returns Full precision percentage string
 *
 * @example
 * getFullPrecisionFundingRate(0.00012345) // "0.012345%"
 */
export function getFullPrecisionFundingRate(fundingRate: number): string {
  const percentage = fundingRate * 100;
  return `${percentage.toFixed(6)}%`;
}
